import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { UserInfoFacade } from '@core/user-info/facades/user-info.facade';
import { ProfileInfoComponent } from '@layout/profile-menu/components/profile-info/profile-info.component';
import { Router, RouterLink } from '@angular/router';
import { NavigationFacade } from '@core/navigation/facades/navigation.facade';
import { userSettingsTopNavigationItem } from '@core/navigation/models/top-navigation/top-navigation-item.interface';
import {
  AvatarComponent,
  MenuKeyboardNavigationDirective,
  PanelTriggerDirective,
  TooltipDirective,
  PrefetchLinkDirective,
} from '@kaseya-design-components';
import { ThemeOptionsListComponent } from '@layout/profile-menu/components/theme-options-list/theme-options-list.component';
import { ApplicationPath } from '@core/navigation/models/application-path.enum';
import { PermissionDirective } from '@core/permissions/directives/permission.directive';
import { Permission } from '@core/permissions/models/permission.enum';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';

@Component({
  selector: 'itc-profile-menu',
  templateUrl: 'profile-menu.component.html',
  styleUrl: 'profile-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    ProfileInfoComponent,
    RouterLink,
    PrefetchLinkDirective,
    PanelTriggerDirective,
    MenuKeyboardNavigationDirective,
    TooltipDirective,
    ThemeOptionsListComponent,
    AvatarComponent,
    PermissionDirective,
  ],
})
export class ProfileMenuComponent {
  private readonly userInfoFacade = inject(UserInfoFacade);
  private readonly navigationFacade = inject(NavigationFacade);
  private readonly permissionsFacade = inject(PermissionsFacade);
  private readonly router = inject(Router);

  readonly userFirstName = this.userInfoFacade.userFirstName;
  readonly userLastName = this.userInfoFacade.userLastName;
  readonly userFullName = computed(() => this.userInfoFacade.userFullName() || this.userInfoFacade.userEmail());
  readonly isActive = computed(() => this.navigationFacade.activeTopNavigation() === userSettingsTopNavigationItem.id);
  readonly userSettingsTopNavigationItem = userSettingsTopNavigationItem;
  readonly ApplicationPath = ApplicationPath;
  readonly Permission = Permission;

  readonly canModifyTheme = this.permissionsFacade.hasPermission(Permission.ThemeSettingsModify);

  isPanelOpened = false;
  isThemePanelOpened = false;

  constructor() {
    this.handleRouteActivation();
  }

  setUserSettingsTopNavigation(): void {
    if (!this.isActive()) {
      this.navigationFacade.setActiveTopNavigation(this.userSettingsTopNavigationItem.id);
    }
  }

  private handleRouteActivation(): void {
    const isActive = this.router.isActive(this.userSettingsTopNavigationItem.id, {
      paths: 'subset',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    });

    if (isActive) {
      this.navigationFacade.setActiveTopNavigation(this.userSettingsTopNavigationItem.id);
    }
  }
}
